import { useCallback, useEffect, useRef, useState } from 'react'
import type { SelectDropdownPosition } from './types'

type SelectDropdownWidthStrategy = 'match-options' | 'preferred'

export type SelectDropdownPositionConfig = {
  widthStrategy: SelectDropdownWidthStrategy
  minMeasuredHeight: number
  minMaxHeight: number
  minViewportWidth?: number
  preferredWidth?: number
}

const viewportMargin = 12
const offset = 8

const calculateDropdownWidth = ({
  buttonWidth,
  optionsWidth,
  windowWidth,
  config,
}: {
  buttonWidth: number
  optionsWidth: number
  windowWidth: number
  config: SelectDropdownPositionConfig
}) => {
  if (config.widthStrategy === 'preferred') {
    const maxViewportWidth = Math.max(config.minViewportWidth ?? 0, Math.floor(windowWidth - viewportMargin * 2))
    const minPreferredWidth = Math.min(config.preferredWidth ?? buttonWidth, maxViewportWidth)
    return Math.min(maxViewportWidth, Math.max(buttonWidth, minPreferredWidth))
  }

  return Math.max(buttonWidth, optionsWidth)
}

/**
 * Owns floating listbox measurement, viewport repositioning, and transition
 * lifecycle state for select components.
 */
export const useSelectDropdownPosition = (config: SelectDropdownPositionConfig) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const optionsRef = useRef<HTMLDivElement | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState<SelectDropdownPosition | null>(null)

  const updateDropdownPosition = useCallback(() => {
    if (typeof window === 'undefined' || !buttonRef.current || !optionsRef.current) return

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const optionsRect = optionsRef.current.getBoundingClientRect()

    const width = calculateDropdownWidth({
      buttonWidth: Math.round(buttonRect.width),
      optionsWidth: Math.round(optionsRect.width),
      windowWidth: window.innerWidth,
      config,
    })
    const measuredHeight = Math.max(config.minMeasuredHeight, Math.round(optionsRect.height))

    let left = buttonRect.left
    left = Math.min(left, window.innerWidth - viewportMargin - width)
    left = Math.max(viewportMargin, left)

    const availableBottom = window.innerHeight - buttonRect.bottom - viewportMargin
    const availableTop = buttonRect.top - viewportMargin
    const placeAbove = availableBottom < measuredHeight + offset && availableTop > availableBottom

    const maxHeight = Math.max(config.minMaxHeight, Math.floor((placeAbove ? availableTop : availableBottom) - offset))
    const renderedHeight = Math.min(measuredHeight, maxHeight)
    const top = placeAbove ? buttonRect.top - renderedHeight - offset : buttonRect.bottom + offset

    setDropdownPosition({
      top: Math.round(Math.max(viewportMargin, top)),
      left: Math.round(left),
      width,
      maxHeight,
      placement: placeAbove ? 'top' : 'bottom',
    })
  }, [config])

  useEffect(() => {
    if (!dropdownOpen) return
    if (typeof window === 'undefined') return

    const rafId = window.requestAnimationFrame(() => {
      updateDropdownPosition()
    })
    const handleViewportChange = () => updateDropdownPosition()
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [dropdownOpen, updateDropdownPosition])

  const beginDropdownTransition = useCallback(() => {
    setDropdownOpen(true)
    setDropdownPosition(null)
  }, [])

  const finishDropdownLeave = useCallback(() => {
    setDropdownOpen(false)
    setDropdownPosition(null)
  }, [])

  return {
    buttonRef,
    optionsRef,
    dropdownPosition,
    beginDropdownTransition,
    finishDropdownLeave,
    updateDropdownPosition,
  }
}
